import {
  OktonSearchSelect,
  OktonPageHeader,
  OktonInput,
  OktonAlert,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  usaControlaDRE,
  useParametros,
  usePlanosDFC,
} from '../../../services/hooks/useCadastros';
import { useContasBancarias } from '../../../services/hooks/useContasBancarias';
import { useCreateTransferencia } from '../../../services/hooks/useTransferencias';
import { limparEntradaValor, valorParaNumero } from '../../../utils/valorInput';
import { EstadoNavegacaoMonitor } from '../../monitor-bancario/estadoNavegacao';

const VAZIO = {
  documento: '',
  origem: '',
  destino: '',
  data: new Date().toISOString().split('T')[0],
  valor: '',
  planocontas: '',
  observacao: '',
};

const NovaTransferencia = () => {
  const history = useHistory();
  const { state } = useLocation<EstadoNavegacaoMonitor | undefined>();
  const destino =
    state?.voltarPara === '/monitor-bancario'
      ? '/monitor-bancario'
      : '/transferencias';
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const { data: contas = [] } = useContasBancarias();
  const { data: planosDFC = [] } = usePlanosDFC();
  const { data: parametros } = useParametros();
  const controlaDRE = usaControlaDRE(parametros);
  const createMutation = useCreateTransferencia();

  const [form, setForm] = useState({ ...VAZIO, origem: state?.conta ?? '' });
  const voltar = () => {
    if (destino === '/monitor-bancario') {
      history.push(destino, {
        conta: state?.conta ?? form.origem,
        periodo: state?.periodo,
      });
    } else {
      history.push(destino);
    }
  };
  const [error, setError] = useState('');

  const salvar = async () => {
    if (!form.documento.trim()) {
      setError('Informe a Referência.');
      return;
    }
    if (!form.origem || !form.destino) {
      setError('Informe a Conta de Origem e a Conta de Destino.');
      return;
    }
    if (form.origem === form.destino) {
      setError('A Conta de Origem deve ser diferente da Conta de Destino.');
      return;
    }
    if (!form.data) {
      setError('Informe a data da Transferência.');
      return;
    }
    const valorNumerico = valorParaNumero(form.valor);
    if (!valorNumerico) {
      setError('Informe um valor maior que zero.');
      return;
    }
    if (controlaDRE && !form.planocontas) {
      setError('Informe o Plano de Contas (DFC).');
      return;
    }
    setError('');
    try {
      await createMutation.mutateAsync({
        documento: form.documento.trim(),
        origem: form.origem,
        destino: form.destino,
        data: form.data,
        valor: String(Math.abs(valorNumerico)),
        planocontas: form.planocontas,
        observacao: form.observacao.trim(),
      });
      voltar();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erro ao gravar a Transferência.',
      );
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Nova Transferência"
        subtitle="Movimentação entre contas próprias"
        onBack={voltar}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Stack spacing="4" maxW="720px">
          {error && <OktonAlert status="error" description={error} />}

          <Box>
            <Text
              as="label"
              htmlFor="transferencia-documento"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Referência *
            </Text>
            <OktonInput
              name="transferencia-documento"
              size="lg"
              rounded="2xl"
              fontWeight="light"
              placeholder="Nº doc / comprovante"
              color={color}
              maxLength={25}
              value={form.documento}
              onChange={e => {
                const documento = e.target.value;
                setForm(f => ({ ...f, documento }));
              }}
            />
          </Box>

          <SimpleGrid columns={2} spacing="4">
            <Box>
              <Text
                as="label"
                htmlFor="origem"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Conta de Origem *
              </Text>
              <OktonSearchSelect
                name="origem"
                placeholder="Selecione a conta"
                value={form.origem}
                onChange={valor => setForm(f => ({ ...f, origem: valor }))}
                options={[
                  ...contas.map(c => ({
                    value: c.id_cts,
                    label: c.descricao_cts,
                  })),
                ]}
              />
            </Box>
            <Box>
              <Text
                as="label"
                htmlFor="destino"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Conta Destino *
              </Text>
              <OktonSearchSelect
                name="destino"
                placeholder="Selecione a conta"
                value={form.destino}
                onChange={valor => setForm(f => ({ ...f, destino: valor }))}
                options={[
                  ...contas.map(c => ({
                    value: c.id_cts,
                    label: c.descricao_cts,
                  })),
                ]}
              />
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={2} spacing="4">
            <Box>
              <Text
                as="label"
                htmlFor="transferencia-data"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Data *
              </Text>
              <OktonInput
                name="transferencia-data"
                type="date"
                size="lg"
                rounded="2xl"
                fontWeight="light"
                color={color}
                value={form.data}
                onChange={e => {
                  const data = e.target.value;
                  setForm(f => ({ ...f, data }));
                }}
              />
            </Box>
            <Box>
              <Text
                as="label"
                htmlFor="transferencia-valor"
                display="block"
                fontSize="sm"
                fontWeight="medium"
                mb="1"
              >
                Valor (R$) *
              </Text>
              <OktonInput
                name="transferencia-valor"
                type="text"
                inputMode="decimal"
                size="lg"
                rounded="2xl"
                fontWeight="light"
                placeholder="0,00"
                color={color}
                value={form.valor}
                onChange={e => {
                  const valor = limparEntradaValor(e.target.value);
                  setForm(f => ({ ...f, valor }));
                }}
              />
            </Box>
          </SimpleGrid>

          <Box>
            <Text
              as="label"
              htmlFor="planocontas"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Plano de Contas (DFC) {controlaDRE && '*'}
            </Text>
            <OktonSearchSelect
              name="planocontas"
              placeholder="Sem classificação"
              value={form.planocontas}
              onChange={valor => setForm(f => ({ ...f, planocontas: valor }))}
              options={[
                ...planosDFC.map(p => ({
                  value: p.id_pct,
                  label: `${p.item_pct} ${p.nome_pct}`,
                })),
              ]}
            />
          </Box>

          <Box>
            <Text
              as="label"
              htmlFor="transferencia-observacao"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Observação
            </Text>
            <OktonInput
              name="transferencia-observacao"
              size="lg"
              rounded="2xl"
              fontWeight="light"
              color={color}
              maxLength={1024}
              value={form.observacao}
              onChange={e => {
                const observacao = e.target.value;
                setForm(f => ({ ...f, observacao }));
              }}
            />
          </Box>

          <Text fontSize="xs" color="gray.500">
            Cadastrada aqui, a Transferência nasce sem conciliação. As duas
            pontas — a saída na origem e a entrada no destino — são conciliadas
            na tela de Conciliação.
          </Text>

          <Flex gridGap="3" pt="2">
            <Button variant="ghost" onClick={voltar}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={
                createMutation.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Check size={16} />
                )
              }
              isDisabled={createMutation.isLoading}
              onClick={salvar}
            >
              Cadastrar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default NovaTransferencia;
